/**
 * Argus — Pagină timeline USB plug/unplug + drive mount/unmount per agent.
 *
 * Bloc continuous-monitoring-usb 2026-05-06.
 */

import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  fetchDeviceEvents,
  DEVICE_EVENT_TYPES,
  DEVICE_EVENT_LABEL,
  DEVICE_EVENT_CUSTOM_EVENT,
  type DeviceEventItem,
  type DeviceEventType,
} from '../lib/deviceEvents';

const EVENT_COLOR: Record<DeviceEventType, 'success' | 'info' | 'warning' | 'error'> = {
  usb_connected: 'success',
  usb_disconnected: 'warning',
  drive_mounted: 'success',
  drive_unmounted: 'info',
};

function formatBytes(n: number | null): string {
  if (n === null) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

export function AgentDevicesPage(): JSX.Element {
  const { agentId } = useParams<{ agentId: string }>();
  const [selectedTypes, setSelectedTypes] = useState<DeviceEventType[]>([]);
  const [serial, setSerial] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [cursor, setCursor] = useState(0);
  const [pages, setPages] = useState<DeviceEventItem[][]>([]);

  const queryClient = useQueryClient();

  const eventsQuery = useQuery({
    queryKey: ['deviceEvents', agentId, selectedTypes, serial, from, to, cursor],
    queryFn: () => {
      const q: Record<string, unknown> = { limit: 50 };
      if (selectedTypes.length > 0) q.event = selectedTypes;
      if (serial) q.serial = serial;
      if (from) q.from = from;
      if (to) q.to = to;
      if (cursor > 0) q.cursor = cursor;
      return fetchDeviceEvents(agentId ?? '', q as Parameters<typeof fetchDeviceEvents>[1]);
    },
    enabled: agentId !== undefined,
    refetchInterval: 30_000,  // back-up polling chiar dacă WS push pierdut
  });

  // Auto-invalidate la primire CustomEvent push pentru acest agent
  useEffect(() => {
    function handler(ev: Event) {
      const ce = ev as CustomEvent<{ agentId: string }>;
      if (ce.detail?.agentId?.toUpperCase() === agentId?.toUpperCase()) {
        // Reset to fresh page + invalidate
        setCursor(0);
        setPages([]);
        void queryClient.invalidateQueries({ queryKey: ['deviceEvents', agentId] });
      }
    }
    window.addEventListener(DEVICE_EVENT_CUSTOM_EVENT, handler);
    return () => window.removeEventListener(DEVICE_EVENT_CUSTOM_EVENT, handler);
  }, [agentId, queryClient]);

  function toggleType(et: DeviceEventType): void {
    setCursor(0);
    setPages([]);
    setSelectedTypes((prev) => (prev.includes(et) ? prev.filter((e) => e !== et) : [...prev, et]));
  }

  function applyFilters(): void {
    setCursor(0);
    setPages([]);
    void eventsQuery.refetch();
  }

  function loadMore(): void {
    if (eventsQuery.data?.nextCursor) {
      const snapshot = [...eventsQuery.data.items];
      setPages((prev) => [...prev, snapshot]);
      setCursor(eventsQuery.data.nextCursor);
    }
  }

  if (agentId === undefined) {
    return <Alert severity="error">agentId lipsă din URL.</Alert>;
  }

  const items = [...pages.flat(), ...(eventsQuery.data?.items ?? [])];

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        USB & Drive events — agent {agentId.slice(0, 8)}…
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {DEVICE_EVENT_TYPES.map((et) => (
              <FormControlLabel
                key={et}
                control={
                  <Checkbox
                    size="small"
                    checked={selectedTypes.includes(et)}
                    onChange={() => toggleType(et)}
                  />
                }
                label={DEVICE_EVENT_LABEL[et]}
              />
            ))}
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <TextField
              label="Serial"
              size="small"
              value={serial}
              onChange={(e) => setSerial(e.target.value)}
              sx={{ minWidth: 200 }}
            />
            <TextField
              label="From (ISO8601)"
              size="small"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              placeholder="2026-05-06 00:00:00"
              sx={{ minWidth: 220 }}
            />
            <TextField
              label="To (ISO8601)"
              size="small"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="2026-05-07 00:00:00"
              sx={{ minWidth: 220 }}
            />
            <Button variant="contained" onClick={applyFilters}>
              Aplică
            </Button>
          </Box>
        </Stack>
      </Paper>

      {eventsQuery.isLoading && <CircularProgress />}
      {eventsQuery.isError && <Alert severity="error">{eventsQuery.error.message}</Alert>}

      {eventsQuery.data && (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Event</TableCell>
                <TableCell>VID:PID</TableCell>
                <TableCell>Serial</TableCell>
                <TableCell>Friendly</TableCell>
                <TableCell>Drive</TableCell>
                <TableCell>FS</TableCell>
                <TableCell>Label</TableCell>
                <TableCell>Size</TableCell>
                <TableCell>By user</TableCell>
                <TableCell>Captured</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((it) => (
                <TableRow key={it.id} hover>
                  <TableCell>
                    <Chip label={DEVICE_EVENT_LABEL[it.event]} size="small" color={EVENT_COLOR[it.event]} />
                  </TableCell>
                  <TableCell>
                    {it.vid && it.pid ? (
                      <code style={{ fontSize: 12 }}>{it.vid}:{it.pid}</code>
                    ) : '—'}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                      {it.serial ?? '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ maxWidth: 240, display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={it.friendlyName ?? ''}>
                      {it.friendlyName ?? '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>{it.driveLetter ?? '—'}</TableCell>
                  <TableCell>{it.filesystem ?? '—'}</TableCell>
                  <TableCell>{it.label ?? '—'}</TableCell>
                  <TableCell>{formatBytes(it.sizeBytes)}</TableCell>
                  <TableCell>
                    <Typography variant="caption">{it.byUser ?? '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{it.capturedAt}</Typography>
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center">Niciun event</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {eventsQuery.data.nextCursor && (
            <Box sx={{ p: 1, textAlign: 'center' }}>
              <Button onClick={loadMore} size="small">Load more</Button>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
}
